import React, { useEffect, useState } from 'react';
import { Building2, Briefcase, ChevronDown, ChevronLeft, Layers, X, ArrowRightIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../components/LanguageContext';
import { useProfileState } from '../components/ProfileStateContext';
import { ROLES } from '../data/roles';
import { SECTORS } from '../data/sectors';
interface Props {
  onContinue: () => void;
  onSkip?: () => void;
  onBack?: () => void;
}
export function OnboardingRole({
  onContinue,
  onSkip,
  onBack
}: Props) {
  const {
    t
  } = useLanguage();
  const {
    updateDraft,
    roleId: initialRoleId,
    roleOther,
    orgName,
    sectorId: initialSectorId,
    sectorOther
  } = useProfileState();
  const [roleId, setRoleId] = useState(initialRoleId || '');
  const [customRole, setCustomRole] = useState(roleOther || '');
  const [isRoleCustom, setIsRoleCustom] = useState(initialRoleId === 'other');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [organization, setOrganization] = useState(orgName || '');
  const [sectorId, setSectorId] = useState(initialSectorId || '');
  const [customSector, setCustomSector] = useState(sectorOther || '');
  const [isSectorDropdownOpen, setIsSectorDropdownOpen] = useState(false);
  const [isSectorCustom, setIsSectorCustom] = useState(initialSectorId === 'other');
  const [mounted, setMounted] = useState(false);
  // Role AND Sector are required; organization is optional
  const sectorFilled = isSectorCustom ? customSector.trim() !== '' : sectorId !== '';
  const roleFilled = isRoleCustom ? customRole.trim() !== '' : roleId !== '';
  const isValid = roleFilled && sectorFilled;
  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(timer);
  }, []);
  const RequiredMark = () => <span className="text-[#C5D82D] ml-0.5" aria-hidden="true" data-id="element-741">
      *
    </span>;
  return <div className="flex flex-col h-full w-full bg-[#0D0D0D] relative" data-id="element-742">
      {onBack && <button onClick={onBack} aria-label="Back" className="absolute top-6 left-6 z-50 w-10 h-10 rounded-full bg-[#1A1A1A]/80 backdrop-blur-md border border-[#2A2A2A] text-[#888888] flex items-center justify-center hover:text-white transition-colors" data-id="element-743">
          <ChevronLeft className="w-6 h-6 -ml-0.5" strokeWidth={2} data-id="element-744" />
        </button>}
      <div className="flex-1 overflow-y-auto px-6 pt-20 pb-48" data-id="element-745">
        {/* Top Progress Bar */}
        <div className="w-full transition-all duration-700 ease-out" style={{
        opacity: mounted ? 1 : 0,
        transitionDelay: '0ms'
      }} data-id="element-746">
          <div className="w-full h-1 bg-[#1A1A1A] rounded-full overflow-hidden" data-id="element-747">
            <div className="h-full bg-[#C5D82D] w-[25%] rounded-full transition-all duration-500" data-id="element-748" />
          </div>
          <div className="flex items-center justify-end mt-1.5" data-id="element-749">
            <span className="text-[#888888] text-[12px]" data-id="element-750">
              {t('role.progress')}
            </span>
          </div>
        </div>

        {/* Heading & Subtext */}
        <div className="mt-6 transition-all duration-700 ease-out" style={{
        opacity: mounted ? 1 : 0,
        transitionDelay: '120ms'
      }} data-id="element-751">
          <h1 className="text-[20px] text-white font-semibold" data-id="element-752">
            {t('role.heading')}
          </h1>
          <p className="text-[14px] text-[#AAAAAA] mt-1" data-id="element-753">{t('role.subtext')}</p>
        </div>

        {/* Form Fields */}
        <div className="mt-6 space-y-5" data-id="element-754">
          {/* Sector Field */}
          <div className={`space-y-2 relative transition-all duration-700 ease-out ${isSectorDropdownOpen ? 'z-50' : 'z-20'}`} style={{
          opacity: mounted ? 1 : 0,
          transitionDelay: '250ms'
        }} data-id="element-755">
            <label className="text-[12px] text-[#888888] flex items-center gap-2 tracking-wide" data-id="element-756">
              <Layers className="w-[14px] h-[14px]" strokeWidth={2} data-id="element-757" />
              {t('role.sectorLabel')}
              <RequiredMark data-id="element-758" />
            </label>

            {!isSectorCustom ? <>
                <button onClick={() => {
              setIsSectorDropdownOpen(!isSectorDropdownOpen);
              setIsDropdownOpen(false);
            }} className="w-full bg-[#1A1A1A] py-3.5 px-4 rounded-xl text-left flex items-center justify-between text-white transition-colors text-[15px]" data-id="element-759">
                  <span className={sectorId ? 'text-white' : 'text-[#555555]'} data-id="element-760">
                    {sectorId ? t(SECTORS.find(s => s.id === sectorId)?.nameKey || '') : t('role.sectorPlaceholder')}
                  </span>
                  <ChevronDown className={`w-5 h-5 text-[#888888] transition-transform duration-200 ${isSectorDropdownOpen ? 'rotate-180' : ''}`} data-id="element-761" />
                </button>

                {/* Sector Dropdown */}
                {isSectorDropdownOpen && <div className="absolute top-[72px] left-0 right-0 bg-[#1A1A1A] rounded-xl border border-[#2A2A2A] overflow-y-auto max-h-[280px] z-50 shadow-xl" data-id="element-762">
                    {SECTORS.map(s => <button key={s.id} onClick={() => {
                if (s.id === 'other') {
                  setIsSectorCustom(true);
                  setSectorId('');
                  setIsSectorDropdownOpen(false);
                } else {
                  setSectorId(s.id);
                  setIsSectorDropdownOpen(false);
                }
              }} className="w-full text-left px-4 py-3 text-[15px] text-white hover:bg-[#242424] transition-colors" data-id="element-763">
                        {t(s.nameKey)}
                      </button>)}
                  </div>}
              </> : <AnimatePresence mode="wait" data-id="element-764">
                <motion.div key="custom-sector" initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.2,
              ease: 'easeInOut'
            }} className="overflow-hidden" data-id="element-765">
                  <div className="relative" data-id="element-766">
                    <input type="text" autoFocus value={customSector} onChange={e => setCustomSector(e.target.value)} placeholder={t('role.sectorOtherPlaceholder')} className="w-full bg-[#1A1A1A] py-3.5 px-4 pr-10 rounded-xl text-white placeholder:text-[#555555] focus:outline-none focus:ring-1 focus:ring-[#2A2A2A] transition-all text-[15px]" data-id="element-767" />
                    <button onClick={() => {
                  setIsSectorCustom(false);
                  setCustomSector('');
                  setSectorId('');
                }} className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-[#888888] hover:text-white transition-colors" aria-label={t('a11y.clearInput')} data-id="element-768">
                      <X className="w-4 h-4" data-id="element-769" />
                    </button>
                  </div>
                </motion.div>
              </AnimatePresence>}
          </div>

          {/* Role Field */}
          <div className={`space-y-2 relative transition-all duration-700 ease-out ${isDropdownOpen ? 'z-50' : 'z-20'}`} style={{
          opacity: mounted ? 1 : 0,
          transitionDelay: '380ms'
        }} data-id="element-770">
            <label className="text-[12px] text-[#888888] flex items-center gap-2 tracking-wide" data-id="element-771">
              <Briefcase className="w-[14px] h-[14px]" strokeWidth={2} data-id="element-772" />
              {t('role.roleLabel')}
              <RequiredMark data-id="element-773" />
            </label>

            {!isRoleCustom ? <>
                <button onClick={() => {
              setIsDropdownOpen(!isDropdownOpen);
              setIsSectorDropdownOpen(false);
            }} className="w-full bg-[#1A1A1A] py-3.5 px-4 rounded-xl text-left flex items-center justify-between text-white transition-colors text-[15px]" data-id="element-774">
                  <span className={roleId ? 'text-white' : 'text-[#555555]'} data-id="element-775">
                    {roleId ? t(ROLES.find(r => r.id === roleId)?.nameKey || '') : t('role.rolePlaceholder')}
                  </span>
                  <ChevronDown className={`w-5 h-5 text-[#888888] transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} data-id="element-776" />
                </button>

                {/* Dropdown — opens downward */}
                {isDropdownOpen && <div className="absolute top-[72px] left-0 right-0 bg-[#1A1A1A] rounded-xl border border-[#2A2A2A] overflow-y-auto max-h-[280px] z-50 shadow-xl" data-id="element-777">
                    {ROLES.map(r => <button key={r.id} onClick={() => {
                if (r.id === 'other') {
                  setIsRoleCustom(true);
                  setRoleId('');
                  setIsDropdownOpen(false);
                } else {
                  setRoleId(r.id);
                  setIsDropdownOpen(false);
                }
              }} className="w-full text-left px-4 py-3 text-[15px] text-white hover:bg-[#242424] transition-colors" data-id="element-778">
                        {t(r.nameKey)}
                      </button>)}
                  </div>}
              </> : <AnimatePresence mode="wait" data-id="element-779">
                <motion.div key="custom-role" initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.2,
              ease: 'easeInOut'
            }} className="overflow-hidden" data-id="element-780">
                  <div className="relative" data-id="element-781">
                    <input type="text" autoFocus value={customRole} onChange={e => setCustomRole(e.target.value)} placeholder={t('role.otherPlaceholder')} className="w-full bg-[#1A1A1A] py-3.5 px-4 pr-10 rounded-xl text-white placeholder:text-[#555555] focus:outline-none focus:ring-1 focus:ring-[#2A2A2A] transition-all text-[15px]" data-id="element-782" />
                    <button onClick={() => {
                  setIsRoleCustom(false);
                  setCustomRole('');
                  setRoleId('');
                }} className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-[#888888] hover:text-white transition-colors" aria-label={t('a11y.clearInput')} data-id="element-783">
                      <X className="w-4 h-4" data-id="element-784" />
                    </button>
                  </div>
                </motion.div>
              </AnimatePresence>}
          </div>

          {/* Organization Field */}
          <div className="space-y-2 transition-all duration-700 ease-out" style={{
          opacity: mounted ? 1 : 0,
          transitionDelay: '500ms'
        }} data-id="element-785">
            <label className="text-[12px] text-[#888888] flex items-center gap-2 tracking-wide" data-id="element-786">
              <Building2 className="w-[14px] h-[14px]" strokeWidth={2} data-id="element-787" />
              {t('role.orgLabel')}
            </label>
            <input type="text" value={organization} onChange={e => setOrganization(e.target.value)} placeholder={t('role.orgPlaceholder')} className="w-full bg-[#1A1A1A] py-3.5 px-4 rounded-xl text-white placeholder:text-[#555555] focus:outline-none focus:ring-1 focus:ring-[#2A2A2A] transition-all text-[15px]" data-id="element-788" />
          </div>
        </div>
      </div>

      {/* Fixed Bottom Bar */}
      <div className="absolute bottom-0 left-0 right-0 bg-[#0D0D0D]/95 backdrop-blur-xl border-t border-[#1A1A1A] px-6 pt-4 pb-8 transition-all duration-700 ease-out z-40" style={{
      opacity: mounted ? 1 : 0,
      transitionDelay: '600ms'
    }} data-id="element-789">
        <div className="flex flex-col" data-id="element-790">
          <button onClick={() => {
          updateDraft({
            roleId: isRoleCustom ? 'other' : roleId,
            roleOther: isRoleCustom ? customRole : '',
            orgName: organization,
            sectorId: isSectorCustom ? 'other' : sectorId,
            sectorOther: isSectorCustom ? customSector : ''
          });
          onContinue();
        }} disabled={!isValid} className={`w-full py-4 rounded-full border-[1.5px] border-[#C5D82D] text-[#C5D82D] bg-transparent text-[14px] font-semibold tracking-wide flex items-center justify-center gap-2 transition-colors hover:bg-[#C5D82D]/10 ${!isValid ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-791">
            {t('role.cta')}
            <ArrowRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-792" />
          </button>
        </div>
      </div>
    </div>;
}